/* eslint-disable no-nested-ternary */
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { FieldProps } from 'formik';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ColorValue,
  NativeSyntheticEvent,
  PixelRatio,
  StyleSheet,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
} from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

/**
 * Default single-line height for a multiline field when no explicit `height`
 * prop is given. Exported so callers that need to reason about this field's
 * rendered height from the outside (e.g. computing a bottom sheet's
 * content-fit size) share this single source of truth instead of
 * re-hardcoding `24` with a comment promising to keep it in sync (dev review,
 * PR #5939).
 */
export const TEXTFIELD_MULTILINE_DEFAULT_HEIGHT = 24;

interface TextFieldProps {
  autoFocus: boolean;
  label: string;
  isFullWidth?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  prefix?: React.ReactChild;
  suffix?: React.ReactChild;
  textInputProps?: TextInputProps;
  isLarge?: boolean;
  showCharsRemaining?: boolean;
  borderless?: boolean;
  isCreateTask?: boolean;
  isLargeText?: boolean;
  remainingTextVar?: keyof typeof theme.textVariants;
  isRemainingTextAtBottom?: boolean;
  /** Pins the remaining-chars counter to the top instead of vertically
   * centering it in the field's box — needed once the box's own height
   * grows with content (e.g. a multiline field), where centering otherwise
   * drifts the counter away from the top-right corner (TA-10919). */
  isRemainingTextAtTop?: boolean;
  marginBottom?: keyof typeof theme.spacing;
  marginTop?: keyof typeof theme.spacing;
  accessibilityLabel?: string;
  defaultValue?: string;
  validateOnChange?: boolean;
  hideError?: boolean;
  containerMaxHeight?: number;
  onChangeText?: (value?: string) => void;
  onBlur?: () => void;
  onFocus?: (b: boolean) => void;
  customBorderColor?: ColorValue;
  customBackgroundColor?: ColorValue;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  editable?: boolean;
  multiline?: boolean;
  height?: number;
  ref?: React.RefObject<TextInput | null>;
  innerRef?: React.RefObject<TextInput | null>;
  autoResizeHeight?: boolean;
  needToShowTailWhenSingleLine?: boolean;
  useBottomSheetInput?: boolean;
}

const TextField: React.FC<TextFieldProps & FieldProps> = ({
  autoFocus,
  label,
  isFullWidth = true,
  isRequired,
  placeholder,
  suffix,
  prefix,
  field,
  textInputProps,
  isLarge,
  marginBottom,
  marginTop,
  showCharsRemaining,
  isRemainingTextAtBottom = false,
  isRemainingTextAtTop = false,
  remainingTextVar,
  form: { touched, errors, setTouched, setFieldValue },
  borderless,
  isCreateTask = false,
  isLargeText,
  accessibilityLabel,
  validateOnChange = true,
  defaultValue,
  hideError,
  containerMaxHeight,
  onChangeText,
  onBlur,
  onFocus,
  customBorderColor,
  customBackgroundColor,
  onKeyPress,
  editable = true,
  multiline = false,
  height,
  ref,
  innerRef,
  autoResizeHeight = false,
  needToShowTailWhenSingleLine = false,
  useBottomSheetInput = false,
}) => {
  const { t } = useTranslation('forms');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(height ?? 48);
  const contentHeightRef = useRef<number>(height ?? 48);
  const shouldAutoResize = autoResizeHeight && Platform.OS === 'web';
  const { style: textInputStyle, ...restTextInputProps } = textInputProps ?? {};
  // Raw OS font scale, deliberately uncapped (unlike theme.getFontScale's
  // MAX_FONT_SCALE=1.3, which only bounds non-text UI like badge/icon sizes).
  // The counter Text below scales fully with the OS setting via its default
  // allowFontScaling, so the space reserved for it has to track the same,
  // real multiplier or a large-text counter outgrows its box and its
  // leading digit lands on top of the input's own text — TA-10714.
  const fontScale = PixelRatio.getFontScale();

  useEffect(() => {
    if (defaultValue) {
      setFieldValue(field.name, defaultValue);
    }
  }, []);
  const getMarginTop = () => {
    if (isLarge) return 's';
    else return marginTop ?? 'xs';
  };

  const getDynamicColors = () => {
    if (Platform.OS === 'android') {
      return {
        color: theme.colors.textPrimary,
        backgroundColor: theme.colors.white,
      };
    }
    return {};
  };

  const ConditionalTextInput = useBottomSheetInput
    ? BottomSheetTextInput
    : TextInput;

  const remainingRightMargin = useMemo(() => {
    if (isRemainingTextAtBottom) return -theme.spacing.xs;
    if (isCreateTask) return 0;
    return theme.spacing.m;
  }, [isRemainingTextAtBottom, isCreateTask]);
  const remainingTextJustifyContent = useMemo(() => {
    if (isRemainingTextAtBottom) return 'flex-end';
    if (isRemainingTextAtTop) return 'flex-start';
    return 'center';
  }, [isRemainingTextAtBottom, isRemainingTextAtTop]);
  return (
    <Box
      width={isFullWidth ? '100%' : undefined}
      marginBottom={
        isCreateTask ? undefined : isLarge ? 'l' : marginBottom || 'm'
      }>
      {label && (
        <Box
          flexDirection='row'
          alignItems='flex-start'
          justifyContent='space-between'
          marginBottom={isCreateTask ? undefined : isLarge ? 's' : 'xs'}>
          {!borderless && <Text variant='labelSmall'>{label}</Text>}
          {isRequired && !borderless && (
            <Text variant='metadataSecondary'>{t('required')}</Text>
          )}
        </Box>
      )}

      <Box
        flexDirection='row'
        style={
          !isLarge && {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }
        }>
        {!!prefix && (
          <Box
            flex={1}
            alignItems='flex-start'
            justifyContent='center'
            style={{ marginRight: -theme.spacing.xl, zIndex: 1 }}>
            {prefix}
          </Box>
        )}
        {(!needToShowTailWhenSingleLine ||
          isFocused ||
          multiline ||
          (field.value ?? '').length == 0) && (
          <ConditionalTextInput
            ref={innerRef ?? ref}
            editable={editable}
            multiline={multiline}
            style={[
              multiline &&
                !shouldAutoResize && {
                  paddingTop: theme.spacing.s,
                  height: height ?? TEXTFIELD_MULTILINE_DEFAULT_HEIGHT,
                },
              borderless && { flex: 1 },
              borderless && isRequired && { minHeight: height ?? 48 },
              !borderless && {
                ...styles.input,
                fontFamily:
                  textInputProps?.secureTextEntry && Platform.OS === 'android'
                    ? undefined
                    : styles.input.fontFamily,
                ...getDynamicColors(),
              },
              // Auto-resize height for multiline on web
              // Must come after styles.input so it overrides the fixed height
              multiline &&
                shouldAutoResize && {
                  paddingTop: theme.spacing.s,
                  height: contentHeight,
                  minHeight: height ?? 48,
                },
              isLargeText && styles.largeText,
              !!prefix && { paddingLeft: 74 },
              !!suffix && { paddingRight: theme.spacing.xxl },
              !!touched[field.name] &&
                !!errors[field.name] && {
                  borderColor: theme.colors.alertRed,
                },
              !!customBorderColor && {
                borderColor: customBorderColor,
              },
              !!customBackgroundColor && {
                backgroundColor: customBackgroundColor,
              },
              showCharsRemaining && {
                paddingRight:
                  (isCreateTask ? theme.spacing.l : theme.spacing.xxl) *
                  fontScale,
              },
              textInputStyle,
            ]}
            onContentSizeChange={(event) => {
              if (multiline && shouldAutoResize) {
                const next = Math.max(
                  Math.ceil(event.nativeEvent.contentSize.height),
                  height ?? 48
                );
                if (next === contentHeightRef.current) return;
                contentHeightRef.current = next;
                queueMicrotask(() => {
                  setContentHeight((prev) => (next === prev ? prev : next));
                });
              }
            }}
            onChangeText={(value) => {
              setTouched({ ...touched, [field.name]: validateOnChange });
              setFieldValue(field.name, value);
              onChangeText?.(value);
            }}
            onBlur={() => {
              setTouched({ ...touched, [field.name]: true });
              setIsFocused(false);
              onBlur?.();
            }}
            autoFocus={autoFocus}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.(true);
            }}
            onKeyPress={onKeyPress}
            value={field.value}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.grey04}
            accessibilityLabel={accessibilityLabel}
            {...restTextInputProps}
          />
        )}

        {needToShowTailWhenSingleLine &&
          !isFocused &&
          !multiline &&
          (field.value ?? '').length > 0 && (
            <Text
              style={[
                borderless && { flex: 1 },
                !borderless && {
                  ...styles.input,
                  paddingTop: 15,

                  ...getDynamicColors(),
                },
                isLargeText && styles.largeText,
                !!prefix && { paddingLeft: 74 },
                !!suffix && { paddingRight: theme.spacing.xxl },
                !!touched[field.name] &&
                  !!errors[field.name] && {
                    borderColor: theme.colors.alertRed,
                  },
                !!customBorderColor && {
                  borderColor: customBorderColor,
                },
                !!customBackgroundColor && {
                  backgroundColor: customBackgroundColor,
                },
              ]}
              onPress={() => {
                setIsFocused(true);
              }}
              numberOfLines={1}
              ellipsizeMode='tail'
              variant='labelSmall'
              accessibilityLabel={accessibilityLabel || label}>
              {field.value || placeholder}
            </Text>
          )}
        {borderless && isRequired && !field.value?.length && (
          <Text variant={isCreateTask ? 'bodySecondary2' : 'metadataSecondary'}>
            {t('required')}
          </Text>
        )}

        {(!!suffix || showCharsRemaining) && (
          <Box
            alignItems='flex-start'
            justifyContent={remainingTextJustifyContent}
            paddingTop={isRemainingTextAtTop ? 'xs' : undefined}
            paddingBottom={isRemainingTextAtBottom ? 'xs' : undefined}
            paddingRight={isRemainingTextAtBottom ? 's' : undefined}
            style={{
              position: 'absolute',
              zIndex: 2,
              ...(containerMaxHeight
                ? {
                    top: -containerMaxHeight / 2,
                    right: -theme.spacing.m,
                  }
                : {
                    top: 0,
                    bottom: 0,
                    right: remainingRightMargin,
                  }),
            }}>
            {showCharsRemaining && textInputProps?.maxLength && isFocused ? (
              <Text
                color='textSecondary'
                // The counter was losing its last digit at certain values —
                // "248" rendering as "24" while "245" was fine. Two things can
                // clip it, and the value only decides which side of the
                // threshold a given number falls on, which is why it looked
                // intermittent:
                //
                // - Without numberOfLines, a counter a hair too wide for its
                //   box wraps, and the box's height is pinned (top: 0,
                //   bottom: 0), so the second line is cut off.
                // - `bodySecondary2` carries letterSpacing, and Android's text
                //   measurement excludes the trailing letter-space, so the
                //   final glyph can fall outside the measured width. The 2pt
                //   of right padding gives it room.
                //
                // Digit widths differ slightly in Inter's proportional
                // figures, so 5 and 8 land either side of that edge.
                numberOfLines={1}
                style={{ paddingRight: theme.spacing.xxxs * fontScale }}
                variant={
                  remainingTextVar ??
                  (isCreateTask ? 'bodySecondary2' : 'bodySecondary')
                }>
                {textInputProps?.maxLength - (field.value?.length ?? 0)}
              </Text>
            ) : (
              suffix
            )}
          </Box>
        )}
      </Box>
      {!!touched[field.name] && !!errors[field.name] && !hideError && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginTop={getMarginTop()}
          marginBottom={
            isCreateTask
              ? Platform.OS === 'ios'
                ? 's'
                : getMarginTop()
              : undefined
          }
          marginRight='m'>
          <Icon
            name='AlertTriangle'
            color='alertRed'
            variant='s'
            marginRight='xxs'
          />
          <Text variant='error'>{errors[field.name]}</Text>
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  input: {
    width: '100%',
    paddingHorizontal: theme.spacing.m,
    borderWidth: 1,
    borderRadius: theme.spacing.xs,
    borderColor: theme.colors.grey02,
    backgroundColor: theme.colors.white,
    fontFamily: 'Inter_400Regular',
    height: 48,
  },
  largeText: {
    ...theme.textVariants.labelLarge,
    color: theme.colors.textPrimary,
  },
});

export default TextField;
